import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import offlineCacheService from '@/services/OfflineCacheService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sound functions using Web Audio API
const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Play two ascending tones for success
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(523.25, now, 0.15); // C5
    playTone(659.25, now + 0.15, 0.15); // E5
    playTone(783.99, now + 0.3, 0.3); // G5
  } catch (e) {
    // Audio not supported in this browser
  }
};

const playErrorSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Play two descending tones for error
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(0.2, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    playTone(400, now, 0.2);
    playTone(300, now + 0.2, 0.3);
  } catch (e) {
    // Audio not supported in this browser
  }
};

const SecurityDashboard = () => {
  const [scanning, setScanning] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [scanner, setScanner] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cachedCount, setCachedCount] = useState(0);
  const [pendingScansCount, setPendingScansCount] = useState(0);
  const { user, logout, token } = useAuth();

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexión restaurada - Sincronizando...');
      offlineCacheService.syncPendingScans();
      refreshCache();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Sin conexión - Modo Offline activado');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Refresh cache function
  const refreshCache = useCallback(async () => {
    if (!token || !navigator.onLine) return;
    
    try {
      const response = await axios.get(`${API}/visitors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.length > 0) {
        await offlineCacheService.cacheVisitors(response.data);
        const count = await offlineCacheService.getCachedVisitorsCount();
        setCachedCount(count);
      }
    } catch (error) {
      // Silently fail cache refresh
    }
  }, [token]);

  // Initial cache load and periodic refresh
  useEffect(() => {
    const loadCacheInfo = async () => {
      const count = await offlineCacheService.getCachedVisitorsCount();
      setCachedCount(count);
      
      const pending = await offlineCacheService.getPendingScans();
      setPendingScansCount(pending.length);
    };
    
    loadCacheInfo();
    
    // Refresh cache every 5 minutes when online
    const interval = setInterval(() => {
      if (navigator.onLine) {
        refreshCache();
        loadCacheInfo();
      }
    }, 5 * 60 * 1000);
    
    // Initial refresh
    refreshCache();
    
    return () => clearInterval(interval);
  }, [refreshCache]);

  useEffect(() => {
    if (scanning) {
      const config = {
        fps: 10,
        qrbox: function(viewfinderWidth, viewfinderHeight) {
          const minEdgePercentage = 0.7;
          const minEdgeSize = Math.min(viewfinderWidth, viewfinderHeight);
          const qrboxSize = Math.floor(minEdgeSize * minEdgePercentage);
          return {
            width: qrboxSize,
            height: qrboxSize
          };
        },
        aspectRatio: 1.0,
        videoConstraints: {
          facingMode: { ideal: "environment" }
        },
        rememberLastUsedCamera: true,
        supportedScanTypes: [],
        showTorchButtonIfSupported: true
      };

      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader-security",
        config,
        false
      );

      html5QrcodeScanner.render(onScanSuccess, onScanError);
      setScanner(html5QrcodeScanner);

      return () => {
        html5QrcodeScanner.clear().catch(() => {});
      };
    }
  }, [scanning]);

  const onScanSuccess = async (decodedText) => {
    if (scanner) {
      scanner.clear().catch(() => {});
    }
    
    setScanning(false);
    
    // Check if online
    if (navigator.onLine) {
      // Online mode - validate with server
      try {
        const response = await axios.post(`${API}/visitors/validate`, {
          qr_code: decodedText,
          scanned_by: user.username
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setValidationResult(response.data);
        
        if (response.data.success) {
          playSuccessSound();
          toast.success(response.data.message);
        } else {
          playErrorSound();
          toast.error(response.data.message);
        }
      } catch (error) {
        // If server error, try offline validation
        if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
          await validateOffline(decodedText);
        } else {
          playErrorSound();
          setValidationResult({
            success: false,
            message: 'Error al validar el código QR'
          });
          toast.error('Error al validar el código QR');
        }
      }
    } else {
      // Offline mode - validate from cache
      await validateOffline(decodedText);
    }
  };

  const validateOffline = async (qrCode) => {
    try {
      const cachedVisitor = await offlineCacheService.getCachedVisitor(qrCode);
      const result = offlineCacheService.validateOffline(cachedVisitor);
      
      setValidationResult(result);
      
      if (result.success) {
        // Store pending scan for later sync
        await offlineCacheService.storePendingScan(
          qrCode, 
          user.username, 
          cachedVisitor, 
          result
        );
        
        const pending = await offlineCacheService.getPendingScans();
        setPendingScansCount(pending.length);
        
        playSuccessSound();
        toast.success(result.message, {
          description: '📴 Validación offline - Se sincronizará al reconectar'
        });
      } else {
        playErrorSound();
        toast.error(result.message, {
          description: result.offline ? '📴 Validación offline' : undefined
        });
      }
    } catch (error) {
      playErrorSound();
      setValidationResult({
        success: false,
        message: 'Error en validación offline',
        offline: true
      });
      toast.error('Error en validación offline');
    }
  };

  const onScanError = (error) => {
    if (error && !error.includes('NotFoundException')) {
      if (error.includes('NotAllowedError') || error.includes('Permission')) {
        setCameraError('Permiso de cámara denegado. Por favor, permite el acceso a la cámara en la configuración de tu navegador.');
        toast.error('Permiso de cámara denegado');
      } else if (error.includes('NotFoundError') || error.includes('No camera')) {
        setCameraError('No se encontró ninguna cámara en este dispositivo.');
        toast.error('Cámara no encontrada');
      }
    }
  };

  const handleScanAgain = () => {
    setValidationResult(null);
    setCameraError(null);
    setScanning(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="security-dashboard-container" data-testid="security-dashboard">
      <div className="admin-header">
        <div className="header-left">
          <h1 data-testid="security-title">Panel de Seguridad</h1>
          <p className="user-info">Usuario: <strong>{user?.username}</strong> | Rol: <strong>Seguridad</strong></p>
        </div>
        <div className="header-actions">
          <Link to="/security/scanned" className="history-button" data-testid="scanned-history-btn">
            📋 Mis Escaneos
          </Link>
          {/* Temporalmente deshabilitados
          <Link to="/security/visitors" className="history-button" data-testid="visitors-btn">
            👥 Visitantes
          </Link>
          <Link to="/security/registry" className="history-button" data-testid="registry-btn">
            📝 Registro
          </Link>
          */}
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Offline Status Indicator */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto 15px auto',
        padding: '0 20px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: isOnline ? '#f0fdf4' : '#fef3c7',
          border: `2px solid ${isOnline ? '#10b981' : '#f59e0b'}`,
          borderRadius: '12px',
          padding: '12px 20px'
        }} data-testid="connection-status">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>{isOnline ? '🌐' : '📴'}</span>
            <div>
              <div style={{ fontWeight: '600', color: isOnline ? '#166534' : '#92400e' }}>
                {isOnline ? 'En Línea' : 'Modo Offline'}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {isOnline 
                  ? 'Validación en tiempo real' 
                  : 'Validación desde cache local'}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13px', color: '#374151' }}>
              <strong>{cachedCount}</strong> visitantes en cache
            </div>
            {pendingScansCount > 0 && (
              <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                <strong>{pendingScansCount}</strong> escaneos pendientes de sincronizar
              </div>
            )}
          </div>
        </div>
        
        {/* Manual refresh button */}
        <button
          onClick={refreshCache}
          disabled={!isOnline}
          style={{
            marginTop: '10px',
            width: '100%',
            padding: '10px',
            background: isOnline ? '#3b82f6' : '#9ca3af',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: isOnline ? 'pointer' : 'not-allowed',
            fontWeight: '500'
          }}
          data-testid="refresh-cache-btn"
        >
          🔄 {isOnline ? 'Actualizar Cache de Visitantes' : 'Requiere conexión'}
        </button>
      </div>

      <div className="security-content">
        <div className="scanner-section">
          <h2>Escanear Código QR</h2>
          
          {scanning && !cameraError && (
            <>
              <p style={{ textAlign: 'center', marginBottom: '1rem', color: '#4a5568' }}>
                Apunta la cámara trasera hacia el código QR
              </p>
              <div id="qr-reader-security" data-testid="qr-reader-security"></div>
            </>
          )}

          {cameraError && (
            <div className="camera-error" data-testid="camera-error">
              <div className="result-card result-error">
                <div className="result-icon">⚠️</div>
                <div className="result-message">{cameraError}</div>
                <button
                  className="btn-scan-again"
                  onClick={handleScanAgain}
                  data-testid="retry-camera-btn"
                >
                  Reintentar
                </button>
              </div>
            </div>
          )}

          {validationResult && (
            <div className="validation-result" data-testid="validation-result">
              <div
                className={`result-card ${
                  validationResult.success ? 'result-success' : 'result-error'
                }`}
              >
                <div className="result-icon">
                  {validationResult.success ? '✅' : '❌'}
                </div>
                <div className="result-message" data-testid="validation-message">
                  {validationResult.message}
                </div>

                {validationResult.visitor && (
                  <div className="result-details" data-testid="visitor-details">
                    <p>
                      <strong>Nombre:</strong> {validationResult.visitor.name}
                    </p>
                    <p>
                      <strong>Tipo:</strong> {validationResult.visitor.visitor_type}
                    </p>
                    <p>
                      <strong>Área:</strong> {validationResult.visitor.area || 'N/A'}
                    </p>
                    {validationResult.visitor.vehicle_plate && (
                      <p>
                        <strong>Placa:</strong> {validationResult.visitor.vehicle_plate}
                      </p>
                    )}
                    {validationResult.visitor.vehicle_brand && (
                      <p>
                        <strong>Marca Vehículo:</strong> {validationResult.visitor.vehicle_brand}
                      </p>
                    )}
                    {validationResult.visitor.vehicle_color && (
                      <p>
                        <strong>Color Vehículo:</strong> {validationResult.visitor.vehicle_color}
                      </p>
                    )}
                    <p>
                      <strong>Creado:</strong> {formatDate(validationResult.visitor.created_at)}
                    </p>
                    {validationResult.visitor.scanned_at && (
                      <p>
                        <strong>Escaneado:</strong> {formatDate(validationResult.visitor.scanned_at)}
                      </p>
                    )}
                  </div>
                )}

                <button
                  className="btn-scan-again"
                  onClick={handleScanAgain}
                  data-testid="scan-again-btn"
                >
                  Escanear Otro
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;
